import {
	Component,
	Element,
	Event,
	EventEmitter,
	h,
	Host,
	Listen,
	Prop,
	Watch,
} from '@stencil/core';
import { IsharesButtonType } from '../_types/button';

export type ButtonClickData = {
	component: HTMLDsButtonElement;
	button: HTMLElement;
	anchor: HTMLElement;
	originalEvent: PointerEvent;
};

export type ButtonColor =
	| ''
	| 'white'
	| 'gray'
	| 'blue'
	| 'purple'
	| 'pink'
	| 'green'
	| 'yellow'
	| 'black';

@Component({
	tag: 'ds-button',
})
export class IsharesButton {
	@Prop({ reflect: true }) color: ButtonColor;
	@Prop({ reflect: true }) type: IsharesButtonType = 'primary';
	@Prop({ reflect: true }) compact: boolean;
	@Prop({ reflect: true }) selected: boolean;
	@Prop({ reflect: true }) disabled = false;
	@Prop({ reflect: true }) fauxDisabled = false; // If you want it to look like it is disabled but keep it clickable

	// 'ariaLabel' provides alternative text for screenreaders to read out instead of what is inside the button or anchor tag.
	@Prop({ reflect: true }) accessibilityLabel: string;

	// Sets the "role" attribute of the focusable element in this component.
	@Prop({ reflect: true }) accessibilityRole?: string; // Eg. 'button', 'tooltip' or 'tab'
	@Prop({ reflect: true }) accessibilitySelected?: boolean; // aria-selected="true"
	@Prop({ reflect: true }) accessibilityControlsId?: string;

	@Prop({ reflect: true }) isLink: boolean;
	@Prop() href = ''; // Optional href for when isLink = true
	@Prop() target = '_self'; // Optional target for when isLink = true

	@Prop({ reflect: true }) showAsActive: boolean;
	@Prop({ reflect: true }) canReceiveFocus = true;

	@Prop({ reflect: true }) dataLinkEvent = ''; // To render data-link-event to the button or anchor tag
	@Prop() noMinWidth = ''; // To remove the CSS min-width from the container (applies to compact buttons only)

	@Event()
	buttonClick: EventEmitter<ButtonClickData>;

	@Element() el: HTMLDsButtonElement;
	anchorEl: HTMLAnchorElement;
	buttonEl: HTMLButtonElement;

	@Listen('click')
	handleClick(event: PointerEvent) {
		event.stopPropagation();
		if (!this.disabled) {
			this.buttonClick.emit({
				component: this.el,
				button: this.buttonEl,
				anchor: this.anchorEl,
				originalEvent: event,
			});
		}
	}

	// used with tab-group, temporarily setting unselected on previous tab
	@Watch('selected')
	handleSelect(newValue, oldValue) {
		if (!newValue && oldValue) this.el.setAttribute('unselected', 'true');
		setTimeout(() => {
			this.el.removeAttribute('unselected');
		}, 400);
	}

	handleKeyDown(event: KeyboardEvent) {
		if (event.code === 'Enter' || event.code === 'Space') {
			if (this.anchorEl) {
				this.anchorEl.click();
			}
			if (this.buttonEl) {
				this.buttonEl.click();
			}
			event.preventDefault();
		}
	}

	render() {
		const props = {
			class: 'ds-button-container',
			'aria-label': this.accessibilityLabel,
			'data-link-event': this.dataLinkEvent,
			tabIndex: this.canReceiveFocus && !this.disabled ? 0 : -1,
			onKeyDown: (event) => this.handleKeyDown(event),
			role: this.accessibilityRole,
			'aria-controls': this.accessibilityControlsId,
		};

		if (this.noMinWidth) {
			props['no-min-width'] = true;
		}

		if (this.isLink) {
			return (
				<Host>
					<a
						href={this.href}
						target={this.target}
						aria-selected={this.accessibilitySelected}
						ref={(el) => (this.anchorEl = el as HTMLAnchorElement)}
						{...props}
					>
						<span class="ds-button-inner-focus">
							<slot />
						</span>
					</a>
				</Host>
			);
		} else {
			return (
				<Host>
					<button
						disabled={this.disabled}
						aria-selected={
							typeof this.accessibilitySelected === 'boolean'
								? String(this.accessibilitySelected)
								: undefined
						}
						ref={(el) => (this.buttonEl = el as HTMLButtonElement)}
						{...props}
					>
						<span class="ds-button-inner-focus">
							<slot />
						</span>
					</button>
				</Host>
			);
		}
	}
}
